import React, { useEffect, useState } from "react";
import {
    AnalyticsDashboardListData,
    DailyAnalyticsType,
    DailyDashboardListElement,
    DashboardAnalyticsListInput,
    MeData,
    OrganiazationType,
    Organization
} from "../../../models/types";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
    GET_DAILY_DASHBOARD_LIST,
    GET_ME,
    GET_ORGANIZATIONS
} from "../../../graphql/queries";
import {
    Button,
    Card,
    Col,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SearchableDropdown } from "../../dropdowns/SearchableDropdown";
import { PaginatorHorizontal } from "../../lists/PaginatorHorizontal";
import { AnalyticsBuildPreviewSimple } from "../../views/analytics/AnalyticsBuildPreviewSimple";
import { faPause, faPlay, faShare } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import styles from "../../views/analytics/AnalyticsBuildPreview.module.scss";
import cx from "classnames";

const convertSelectionAnalyticsType = (
    selection: string
): DailyAnalyticsType | undefined => {
    switch (selection) {
        case "Past 7 days":
            return DailyAnalyticsType.LastWeek;
        case "Last calendar week":
            return DailyAnalyticsType.LastCalendarWeek;
        case "Past 30 days":
            return DailyAnalyticsType.LastMonth;
        case "Last calendar month":
            return DailyAnalyticsType.LastCalendarMonth;
        case "Past 90 days":
            return DailyAnalyticsType.LastQuarter;
        case "Last calendar quarter":
            return DailyAnalyticsType.LastCalendarQuarter;
        case "Past 180 days":
            return DailyAnalyticsType.LastHalf;
        case "Last calendar half-year":
            return DailyAnalyticsType.LastCalendarHalf;
        case "Past 365 days":
            return DailyAnalyticsType.LastYear;
        case "Last calendar year":
            return DailyAnalyticsType.LastCalendarYear;
        default:
            return DailyAnalyticsType.AllTime;
    }
};

const convertSelectionSorting = (selection: string): string => {
    switch (selection) {
        case "CTR %":
            return "ctr";
        case "Impressions":
            return "impressions";
        default:
            return "ctr";
    }
};

const AdsPerformanceImplementation = () => {
    const { data: { me } = {} } = useQuery<MeData>(GET_ME, {
        fetchPolicy: "cache-only"
    });
    const [globalPlay, setGlobalPlay] = useState(true);
    const [selectedOrganization, updateSelectedOrganization] = useState<
        Organization | undefined
    >(undefined);
    const [selectedAnalyticsTimeRange, updateSelectedAnalyticsTimeRange] =
        useState<string>("Past 7 days");
    const [selectedAnalyticsSorting, updateSelectedAnalyticsSorting] =
        useState<string>("CTR %");
    const [boardElements, updateBoardElements] = useState<
        DailyDashboardListElement[] | undefined
    >(undefined);
    const { data: { organizations } = {} } = useQuery(GET_ORGANIZATIONS);
    // @ts-ignore
    const [queryDashboardData, { data: { getDashboardAnalytics } = {} }] =
        useLazyQuery<AnalyticsDashboardListData, DashboardAnalyticsListInput>(
            GET_DAILY_DASHBOARD_LIST
        );

    const organizationChoices = ["All clients"];
    for (let i = 0; i < organizations?.length; i++) {
        if (organizations[i].name === "Restricted Organization") {
            continue;
        } else {
            organizationChoices.push(organizations[i].name);
        }
    }

    const getSimpleDate = (date: string) => {
        const dateObj = new Date(date);
        const day = String(dateObj.getDate()).padStart(2, "0");
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const year = dateObj.getFullYear();

        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        if (getDashboardAnalytics !== undefined) {
            const currentResult = getDashboardAnalytics.filter(
                (element: DailyDashboardListElement) =>
                    element.networkBuild.network === "unity" ||
                    element.networkBuild.network === "ironsource"
            );
            updateBoardElements(currentResult);
        }
    }, [getDashboardAnalytics]);

    useEffect(() => {
        queryDashboardData({
            variables: {
                input: {
                    dailyAnalyticsType: convertSelectionAnalyticsType(
                        selectedAnalyticsTimeRange
                    ),
                    organization: selectedOrganization?.id || undefined,
                    sortingType: convertSelectionSorting(
                        selectedAnalyticsSorting
                    ),
                    count: 10,
                    minimumImpressions: 1000
                }
            }
        });
    }, [
        selectedOrganization,
        selectedAnalyticsSorting,
        selectedAnalyticsTimeRange
    ]);

    if (!me || me.organization?.organizationType !== OrganiazationType.Owner) {
        return (
            <div className="fullSize p-2 pt-5 d-flex justify-content-center text-inverse-50">
                <p>You lack the necessary permissions to view this content.</p>
            </div>
        );
    }

    const renderedBoardElements =
        boardElements && boardElements?.length > 0
            ? boardElements.map(
                  (element: DailyDashboardListElement, index: number) => {
                      const buildReportUrl = `/products/${element.networkBuild.build?.revision.project.product?.id}/projects/${element.networkBuild.build?.revision.project.id}/revisions/${element.networkBuild.build?.revision.id}/history/${element.networkBuild.build?.id}`;
                      const dateRange =
                          element.startDate && element.endDate
                              ? `${getSimpleDate(element.startDate)} — ${getSimpleDate(element.endDate)}`
                              : "All time";

                      return (
                          <Card
                              key={index}
                              className={cx(
                                  "text-inverse inner-card",
                                  styles.previewCard
                              )}
                          >
                              <Card.Header className={styles.previewCardHeader}>
                                  <h6 className="d-flex mt-1">
                                      {
                                          element.networkBuild.build?.revision
                                              ?.project?.product?.name
                                      }
                                      <OverlayTrigger
                                          placement="top"
                                          delay={{ show: 500, hide: 100 }}
                                          overlay={
                                              <Tooltip>
                                                  Show ad build report in new
                                                  tab
                                              </Tooltip>
                                          }
                                      >
                                          <Link
                                              to={buildReportUrl}
                                              target="_blank"
                                              className="text-inverse-50 mouseHover text-end ms-auto"
                                          >
                                              <FontAwesomeIcon icon={faShare} />
                                          </Link>
                                      </OverlayTrigger>
                                  </h6>
                                  <p>
                                      {
                                          element.networkBuild.build?.revision
                                              ?.project?.product?.organization
                                              ?.name
                                      }
                                  </p>
                                  <OverlayTrigger
                                      placement="top"
                                      delay={{ show: 500, hide: 100 }}
                                      overlay={
                                          <Tooltip>
                                              {element.networkBuild.filename}
                                          </Tooltip>
                                      }
                                  >
                                      <p className="medium-font">
                                          {
                                              element.networkBuild.build
                                                  ?.revision?.project?.name
                                          }{" "}
                                          / {element.networkBuild.variationName}
                                      </p>
                                  </OverlayTrigger>
                              </Card.Header>
                              <Card.Body className="py-1">
                                  <AnalyticsBuildPreviewSimple
                                      key={element.networkBuild.id}
                                      networkBuild={element.networkBuild}
                                      globalPlay={globalPlay}
                                  />
                              </Card.Body>
                              <Card.Footer className="pb-0">
                                  <Row className="medium-font">
                                      <div>
                                          Impressions &mdash;{" "}
                                          {element.allImpressions.toLocaleString()}
                                      </div>
                                      <div>
                                          CTR &mdash;{" "}
                                          {(element.ctr * 100)
                                              .toFixed(2)
                                              .toLocaleString()}{" "}
                                          %
                                      </div>
                                      <OverlayTrigger
                                          delay={{ show: 500, hide: 100 }}
                                          overlay={
                                              <Tooltip id="tooltip-date-range">
                                                  Selected date range
                                              </Tooltip>
                                          }
                                      >
                                          <p className="small-font text-inverse-75 pt-3 pb-0">
                                              {dateRange}
                                          </p>
                                      </OverlayTrigger>
                                  </Row>
                              </Card.Footer>
                          </Card>
                      );
                  }
              )
            : [
                  <div
                      key="empty-state"
                      className="d-flex w-100 justify-content-center ms-5 py-5"
                  >
                      <p className="text-inverse-50">
                          No performance data found.
                      </p>
                  </div>
              ];

    return (
        <div>
            <Row className="mt-4 mb-3 mb-xl-4 ps-4">
                <Col className="col-12 col-lg-auto me-3">
                    <h5 className="text-inverse-75 mb-3 mb-lg-0">
                        Best Performing Seepia Playables
                    </h5>
                </Col>
                <Col xs={"auto"}>
                    <SearchableDropdown
                        variant="primary"
                        choices={organizationChoices}
                        selectedValue={
                            selectedOrganization
                                ? selectedOrganization.name
                                : "All clients"
                        }
                        placeholder="Select client"
                        onSelect={(selection: string) => {
                            for (let i = 0; i < organizations.length; i++) {
                                if (selection === organizations[i].name) {
                                    updateSelectedOrganization(
                                        organizations[i]
                                    );
                                    return;
                                }
                            }
                            updateSelectedOrganization(undefined);
                        }}
                    />
                </Col>
                <Col xs={"auto"}>
                    <Dropdown>
                        <Dropdown.Toggle className={styles.dropDownToggle}>
                            {selectedAnalyticsSorting}
                        </Dropdown.Toggle>
                        <DropdownMenu>
                            {["CTR %", "Impressions"].map(choice => (
                                <DropdownItem
                                    key={choice}
                                    onClick={() =>
                                        updateSelectedAnalyticsSorting(choice)
                                    }
                                >
                                    {choice}
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </Dropdown>
                </Col>
                <Col className="mt-3 mt-sm-0 col-6 col-sm-auto">
                    <Dropdown>
                        <Dropdown.Toggle className={styles.dropDownToggle}>
                            {selectedAnalyticsTimeRange}
                        </Dropdown.Toggle>
                        <DropdownMenu>
                            <Dropdown.Header>
                                <span className="text-inverse-50">
                                    Past ...
                                </span>
                            </Dropdown.Header>
                            {[
                                "7 days",
                                "30 days",
                                "90 days",
                                "180 days",
                                "365 days"
                            ].map(choice => (
                                <DropdownItem
                                    key={choice}
                                    active={
                                        selectedAnalyticsTimeRange ===
                                        `Past ${choice}`
                                    }
                                    onClick={() =>
                                        updateSelectedAnalyticsTimeRange(
                                            `Past ${choice}`
                                        )
                                    }
                                >
                                    {choice}
                                </DropdownItem>
                            ))}
                            <Dropdown.Divider />
                            <Dropdown.Header>
                                <span className="text-inverse-50">
                                    Last calendar ...
                                </span>
                            </Dropdown.Header>
                            {[
                                "week",
                                "month",
                                "quarter",
                                "half-year",
                                "year"
                            ].map(choice => (
                                <DropdownItem
                                    key={choice}
                                    active={
                                        selectedAnalyticsTimeRange ===
                                        `Last calendar ${choice}`
                                    }
                                    onClick={() =>
                                        updateSelectedAnalyticsTimeRange(
                                            `Last calendar ${choice}`
                                        )
                                    }
                                >
                                    {choice}
                                </DropdownItem>
                            ))}
                            <Dropdown.Divider className="text-inverse" />
                            <DropdownItem
                                active={
                                    selectedAnalyticsTimeRange === "All time"
                                }
                                onClick={() =>
                                    updateSelectedAnalyticsTimeRange("All time")
                                }
                            >
                                All time
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </Col>
                <Col className="d-flex justify-content-center align-items-center mt-3 mt-sm-0 col-6 col-sm-auto">
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 300, hide: 100 }}
                        overlay={
                            <Tooltip>
                                {globalPlay ? "Stop All" : "Start All"}
                            </Tooltip>
                        }
                    >
                        <Button
                            onClick={() => {
                                setGlobalPlay(!globalPlay);
                            }}
                            variant={
                                globalPlay
                                    ? "outline-danger"
                                    : "outline-success"
                            }
                            className={styles.globalPlayBtn}
                        >
                            {globalPlay ? (
                                <>
                                    <FontAwesomeIcon
                                        icon={faPause}
                                        size="xl"
                                        className="text-inverse-75"
                                    />
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faPlay}
                                        size="xl"
                                        className="text-inverse-75"
                                    />
                                </>
                            )}
                        </Button>
                    </OverlayTrigger>
                </Col>
            </Row>
            <Row>
                {renderedBoardElements !== undefined ? (
                    <PaginatorHorizontal
                        list={renderedBoardElements}
                        maxElementCount={5}
                        activeItem={undefined}
                    />
                ) : null}
            </Row>
            <Row>
                <Col className="text-inverse-75 small-font mt-4 mb-2">
                    <div className="text-center">
                        Leaderboard results are indicative, and as such, meant
                        for internal retrospection rather than analytical
                        purposes. Use the dedicated Seepia analytical tools for
                        accurate data analysis.
                    </div>{" "}
                    <div className="text-center">
                        Playable ads distributed in Unity and Ironsource
                        networks are included in the leaderboards.
                    </div>
                </Col>
            </Row>
        </div>
    );
};
export const AdsPerformance = React.memo(AdsPerformanceImplementation);
